/**
 * HeaderMetricTile.tsx — MER Algo Footprints v9.2.0
 *
 * Phase 2: Global Trading Header — Row 2 (Decision Context Bar).
 *
 * Generic tile rendering label + value + optional note. Used 5+ times in
 * Row 2 for regime, posture, confidence, rvol, key level, gamma.
 *
 * Empty-state contract (Phase 2 spec Step 9):
 *   value can be null/undefined; the tile renders the empty-state string
 *   ("Loading..." or "No data") supplied by the caller.
 *
 * Pure presentational — no hooks.
 */

import React, { ReactNode } from 'react';

export interface HeaderMetricTileProps {
  label:     string;          // small uppercase header (e.g. 'REGIME')
  value:     ReactNode;       // primary content — string, formatted number, or pill
  note?:     ReactNode;       // optional secondary line under value
  align?:    'left' | 'center';
  minWidth?: number;          // keep tiles visually balanced
  className?: string;
  title?:    string;
}

export function HeaderMetricTile({
  label,
  value,
  note,
  align = 'left',
  minWidth,
  className,
  title,
}: HeaderMetricTileProps) {
  return (
    <div
      className={['mer-metric-tile', className].filter(Boolean).join(' ')}
      title={title}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
        padding: '0 14px',
        borderRight: '1px solid var(--mer-border)',
        height: '100%',
        minWidth,
        textAlign: align,
        alignItems: align === 'center' ? 'center' : 'flex-start',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: 1,
          color: 'var(--mer-label)',
          textTransform: 'uppercase',
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 16,
          color: 'var(--mer-text)',
          fontWeight: 600,
          lineHeight: 1.2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
      {note != null && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--mer-muted)',
            fontWeight: 400,
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {note}
        </span>
      )}
    </div>
  );
}
